(() => {
  if (customElements.get("onura-bundle-cart-group")) return;

  const getRoot = () => window.Shopify?.routes?.root || "/";

  const parseJsonResponse = async (response) => {
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(
        payload?.description || payload?.message || window.cartStrings?.error || "Unable to update the cart."
      );
    }

    return payload;
  };

  const fetchCart = async () => {
    const response = await fetch(`${getRoot()}cart.js`, {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });

    return parseJsonResponse(response);
  };

  const getSectionsToRender = () => {
    const targets = [];
    const cartContainers = [
      document.querySelector("cart-drawer-items"),
      document.querySelector("cart-items")
    ].filter(Boolean);

    cartContainers.forEach((container) => {
      if (typeof container.getSectionsToRender !== "function") return;

      try {
        targets.push(...container.getSectionsToRender());
      } catch (error) {
        console.warn("[ONURA bundle] Unable to read a cart section target.", error);
      }
    });

    if (!targets.length && document.getElementById("CartDrawer")) {
      targets.push(
        { id: "CartDrawer", section: "cart-drawer", selector: ".drawer__inner" },
        { id: "cart-icon-bubble", section: "cart-icon-bubble" }
      );
    }

    const seenTargets = new Set();

    return targets.filter((target) => {
      const section = target.section || target.id;
      const signature = `${target.id}|${section}|${target.selector || ""}`;

      if (!target.id || !section || seenTargets.has(signature)) return false;

      seenTargets.add(signature);
      return true;
    });
  };

  const getRequestedSections = (targets) => {
    return [...new Set(targets.map((target) => target.section || target.id).filter(Boolean))];
  };

  const getSourceElement = (documentFragment, target, targetElement) => {
    if (target.selector) {
      const nestedTarget = targetElement.matches(target.selector)
        ? targetElement
        : targetElement.querySelector(target.selector);

      if (nestedTarget) {
        return {
          source: documentFragment.querySelector(target.selector),
          destination: nestedTarget
        };
      }
    }

    return {
      source: documentFragment.getElementById(target.id) || documentFragment.querySelector(".shopify-section"),
      destination: targetElement
    };
  };

  const updateCartShellState = (cartState) => {
    const isEmpty = Number(cartState.item_count) === 0;

    document.querySelector("cart-drawer")?.classList.toggle("is-empty", isEmpty);
    document.querySelector("cart-drawer-items")?.classList.toggle("is-empty", isEmpty);
    document.querySelector("cart-items")?.classList.toggle("is-empty", isEmpty);
    document.getElementById("main-cart-footer")?.classList.toggle("is-empty", isEmpty);

    document.querySelectorAll("[data-onura-cart-count]").forEach((count) => {
      count.textContent = String(cartState.item_count || 0);
      count.classList.toggle("is-empty", isEmpty);
      count.setAttribute("aria-label", `${cartState.item_count || 0} items in cart`);
    });
  };

  const renderCartSections = (cartState, targets) => {
    if (!cartState?.sections) return false;

    let renderedSection = false;

    targets.forEach((target) => {
      const section = target.section || target.id;
      const sectionHtml = cartState.sections[section] || cartState.sections[target.id];
      const targetElement = document.getElementById(target.id);

      if (!sectionHtml || !targetElement) return;

      const documentFragment = new DOMParser().parseFromString(sectionHtml, "text/html");
      const { source, destination } = getSourceElement(documentFragment, target, targetElement);

      if (!source || !destination) return;

      destination.innerHTML = source.innerHTML;
      renderedSection = true;
    });

    updateCartShellState(cartState);
    return renderedSection;
  };

  const requestSectionHtml = async (sections) => {
    if (!sections.length) return {};

    const sectionUrl = new URL(`${getRoot()}cart`, window.location.origin);
    sectionUrl.searchParams.set("sections", sections.join(","));

    const response = await fetch(sectionUrl.toString(), {
      method: "GET",
      credentials: "same-origin",
      headers: { Accept: "application/json" }
    });

    return parseJsonResponse(response);
  };

  const recoverCartState = async (targets) => {
    const sections = getRequestedSections(targets);
    const [cartState, sectionHtml] = await Promise.all([fetchCart(), requestSectionHtml(sections)]);
    cartState.sections = sectionHtml;
    renderCartSections(cartState, targets);
    return cartState;
  };

  const getBundleLines = (cart, bundleId) => {
    return cart.items.filter((item) => item.properties?._onura_bundle_id === bundleId);
  };

  const getComponentName = (item) => {
    return item.properties?._onura_bundle_component || item.product_title || String(item.variant_id || item.id);
  };

  const getBundleGroups = (cart, bundleId) => {
    const groups = new Map();

    getBundleLines(cart, bundleId).forEach((item) => {
      const componentName = getComponentName(item);
      const group = groups.get(componentName) || {
        componentName,
        bundleName: item.properties?._onura_bundle_name || "ONURA bundle",
        variantId: Number(item.variant_id || item.id),
        lines: [],
        totalQuantity: 0
      };

      group.lines.push(item);
      group.totalQuantity += Number(item.quantity) || 0;
      groups.set(componentName, group);
    });

    return groups;
  };

  const getBundleQuantity = (cart, bundleId) => {
    const groups = getBundleGroups(cart, bundleId);
    if (!groups.size) return null;

    const totals = Array.from(groups.values(), (group) => group.totalQuantity);
    const quantity = Math.min(...totals);

    if (new Set(totals).size > 1) {
      console.warn("[ONURA bundle] Component quantities are inconsistent; using the lowest total.", {
        bundleId,
        totals
      });
    }

    return quantity;
  };

  const postCartMutation = async (path, body) => {
    const response = await fetch(`${getRoot()}${path}`, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json"
      },
      body: JSON.stringify(body)
    });

    return parseJsonResponse(response);
  };

  const getReductionUpdates = (groups, targetQuantity) => {
    const updates = {};

    groups.forEach((group) => {
      if (group.totalQuantity <= targetQuantity) return;

      let remainingQuantity = targetQuantity;
      group.lines.forEach((item) => {
        const currentLineQuantity = Number(item.quantity) || 0;
        const nextLineQuantity = Math.min(currentLineQuantity, remainingQuantity);
        remainingQuantity -= nextLineQuantity;

        if (nextLineQuantity !== currentLineQuantity) {
          updates[item.key] = nextLineQuantity;
        }
      });
    });

    return updates;
  };

  const getAdditionItems = (groups, targetQuantity) => {
    const items = [];

    groups.forEach((group) => {
      const delta = targetQuantity - group.totalQuantity;
      if (delta <= 0) return;

      items.push({
        id: group.variantId,
        quantity: delta,
        properties: {
          _onura_bundle_id: group.lines[0].properties._onura_bundle_id,
          _onura_bundle_name: group.bundleName,
          _onura_bundle_component: group.componentName
        }
      });
    });

    return items;
  };

  const updateBundleLines = async (bundleId, targetQuantity, targets, currentCart) => {
    let freshCart = currentCart;
    let groups = getBundleGroups(freshCart, bundleId);

    if (!groups.size) {
      throw new Error(window.cartStrings?.error || "This bundle is no longer in the cart.");
    }

    const expectedComponents = new Set(groups.keys());
    const reductionUpdates = getReductionUpdates(groups, targetQuantity);

    if (Object.keys(reductionUpdates).length) {
      await postCartMutation("cart/update.js", { updates: reductionUpdates });
      freshCart = await fetchCart();
      groups = getBundleGroups(freshCart, bundleId);
    }

    if (targetQuantity > 0) {
      const additionItems = getAdditionItems(groups, targetQuantity);
      if (additionItems.length) {
        await postCartMutation("cart/add.js", { items: additionItems });
      }
    }

    const requestedSections = getRequestedSections(targets);
    const [updatedCart, sectionHtml] = await Promise.all([
      fetchCart(),
      requestSectionHtml(requestedSections)
    ]);
    updatedCart.sections = sectionHtml;

    const updatedGroups = getBundleGroups(updatedCart, bundleId);
    const isSynchronized = targetQuantity === 0
      ? updatedGroups.size === 0
      : updatedGroups.size === expectedComponents.size &&
        Array.from(expectedComponents).every((componentName) => {
          return updatedGroups.get(componentName)?.totalQuantity === targetQuantity;
        });

    const renderedSections = renderCartSections(updatedCart, targets);

    if (!renderedSections && requestedSections.length) {
      updatedCart.sections = await requestSectionHtml(requestedSections);
      renderCartSections(updatedCart, targets);
    }

    if (!isSynchronized) {
      throw new Error(window.cartStrings?.error || "The bundle quantity could not be fully updated.");
    }

    return updatedCart;
  };

  const findBundleGroup = (bundleId) => {
    return Array.from(document.querySelectorAll("[data-onura-bundle-cart-group]")).find((group) => {
      return group.getAttribute("data-onura-bundle-id") === bundleId;
    });
  };

  class OnuraBundleCartGroup extends HTMLElement {
    constructor() {
      super();
      this.isUpdating = false;
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.addEventListener("click", this.handleClick);
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
    }

    setBusy(isBusy) {
      this.isUpdating = isBusy;
      this.classList.toggle("is-loading", isBusy);
      this.setAttribute("aria-busy", String(isBusy));
      this.querySelectorAll("[data-onura-bundle-cart-action]").forEach((control) => {
        control.disabled = isBusy || control.hasAttribute("data-onura-bundle-control-disabled");
      });
    }

    showError(message) {
      const status = this.querySelector("[data-onura-bundle-cart-status]");
      if (!status) return;

      status.textContent = message;
      status.hidden = !message;
    }

    getFreshQuantity(cart, bundleId) {
      return getBundleQuantity(cart, bundleId);
    }

    async handleClick(event) {
      const control = event.target.closest("[data-onura-bundle-cart-action]");
      if (!control || !this.contains(control) || this.isUpdating || control.disabled) return;

      const action = control.getAttribute("data-onura-bundle-cart-action");
      const bundleId = this.getAttribute("data-onura-bundle-id");
      if (!bundleId || !["increase", "decrease", "remove"].includes(action)) return;

      event.preventDefault();
      this.setBusy(true);
      this.showError("");

      const targets = getSectionsToRender();

      try {
        const currentCart = await fetchCart();
        const currentQuantity = this.getFreshQuantity(currentCart, bundleId);

        if (currentQuantity === null) {
          throw new Error(window.cartStrings?.error || "This bundle is no longer in the cart.");
        }

        const targetQuantity =
          action === "remove" ? 0 : Math.max(0, currentQuantity + (action === "increase" ? 1 : -1));

        await updateBundleLines(bundleId, targetQuantity, targets, currentCart);

        const refreshedGroup = findBundleGroup(bundleId);
        const focusTarget = refreshedGroup?.querySelector(`[data-onura-bundle-cart-action="${action}"]`);
        focusTarget?.focus();
      } catch (error) {
        try {
          await recoverCartState(targets);
        } catch (recoveryError) {
          console.error("[ONURA bundle] Unable to recover cart sections.", recoveryError);
        }

        const refreshedGroup = findBundleGroup(bundleId);
        if (refreshedGroup && typeof refreshedGroup.showError === "function") {
          refreshedGroup.showError(error.message);
          refreshedGroup.setBusy(false);
        } else {
          const globalError = document.getElementById("cart-errors") || document.getElementById("CartDrawer-CartErrors");
          if (globalError) globalError.textContent = error.message;
        }

        console.error("[ONURA bundle] Cart update failed.", error);
      } finally {
        if (this.isConnected) this.setBusy(false);
      }
    }
  }

  customElements.define("onura-bundle-cart-group", OnuraBundleCartGroup);
})();
